import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getMediaTypeFromUrl, getImageUrl } from '../../../utils/helpers';

const InstagramPreview = ({ 
  contentType, 
  imageUrl, 
  caption,
  username = 'username',
  aspectRatio = '1:1',
  slidesCount = 1,
  carouselItems = []
}) => {
  const { isRTL } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Use carouselItems if available, otherwise fall back to imageUrl
  const hasCarouselItems = carouselItems && carouselItems.length > 0;
  const effectiveSlidesCount = hasCarouselItems ? carouselItems.length : slidesCount;
  
  // Get current slide media
  const getCurrentMedia = () => {
    if (hasCarouselItems && carouselItems[currentSlide]) {
      return {
        url: getImageUrl(carouselItems[currentSlide].url),
        type: carouselItems[currentSlide].type || 'image'
      };
    }
    return {
      url: imageUrl ? getImageUrl(imageUrl) : null,
      type: imageUrl ? (getMediaTypeFromUrl(imageUrl) === 'video' ? 'video' : 'image') : 'image'
    };
  };
  
  const currentMedia = getCurrentMedia();
  
  const handlePreviousSlide = (e) => {
    e.stopPropagation();
    if (hasCarouselItems) {
      setCurrentSlide((prev) => (prev === 0 ? carouselItems.length - 1 : prev - 1));
    }
  };
  
  const handleNextSlide = (e) => {
    e.stopPropagation();
    if (hasCarouselItems) {
      setCurrentSlide((prev) => (prev === carouselItems.length - 1 ? 0 : prev + 1));
    }
  };
  
  const handleDotClick = (index) => {
    if (hasCarouselItems) {
      setCurrentSlide(index);
    }
  };
  
  // Calculate aspect ratio for image container
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '1:1':
        return 'aspect-square';
      case '4:5':
        return 'aspect-[4/5]';
      case '1.91:1':
        return 'aspect-[1.91/1]';
      case '9:16':
        return 'aspect-[9/16]';
      default:
        return 'aspect-square';
    }
  };

  // Story Preview
  if (contentType === 'story') {
    return (
      <div className="w-full max-w-[280px] mx-auto">
        <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl">
          {/* Phone Frame */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full z-20"></div>
          
          {/* Story Content */}
          <div className="aspect-[9/16] relative">
            {/* Progress Bar */}
            <div className="absolute top-4 left-2 right-2 z-10 flex gap-1">
              <div className="flex-1 h-0.5 bg-white/50 rounded-full overflow-hidden">
                <div className="h-full w-1/2 bg-white rounded-full"></div>
              </div>
            </div>
            
            {/* Header */}
            <div className="absolute top-8 left-3 right-3 z-10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 p-0.5">
                  <div className="w-full h-full rounded-full bg-gray-800"></div>
                </div>
                <span className="text-white text-sm font-medium">{username}</span>
                <span className="text-white/60 text-xs">2h</span>
              </div>
              <button className="text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Image/Video Content */}
            {imageUrl ? (
              getMediaTypeFromUrl(imageUrl) === 'video' ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt="Story" className="w-full h-full object-cover" />
              )
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                <span className="text-white/50 text-sm">Story Preview</span>
              </div>
            )}
            
            {/* Bottom Actions */}
            <div className="absolute bottom-4 left-3 right-3 z-10">
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  placeholder="Send message" 
                  className="flex-1 bg-white/20 text-white placeholder-white/60 text-sm px-4 py-2 rounded-full border border-white/30"
                  disabled
                />
                <button className="text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
                <button className="text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">Instagram Story Preview</p>
      </div>
    );
  }

  // Reel Preview
  if (contentType === 'reel') {
    return (
      <div className="w-full max-w-[280px] mx-auto">
        <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl">
          {/* Phone Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full z-20"></div>
          
          {/* Reel Content */}
          <div className="aspect-[9/16] relative">
            {/* Image/Video Content */}
            {imageUrl ? (
              getMediaTypeFromUrl(imageUrl) === 'video' ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt="Reel" className="w-full h-full object-cover" />
              )
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-900 via-pink-800 to-red-700 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-6xl">🎬</span>
                  <p className="text-white/50 text-sm mt-2">Reel Preview</p>
                </div>
              </div>
            )}
            
            {/* Right Side Actions */}
            <div className="absolute right-3 bottom-24 z-10 flex flex-col items-center gap-4">
              <button className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                </div>
                <span className="text-white text-xs mt-1">12.5K</span>
              </button>
              <button className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <span className="text-white text-xs mt-1">842</span>
              </button>
              <button className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </div>
              </button>
              <button className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                  </svg>
                </div>
              </button>
              <div className="w-8 h-8 rounded border-2 border-white overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500"></div>
              </div>
            </div>
            
            {/* Bottom Info */}
            <div className="absolute left-3 right-14 bottom-4 z-10">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gray-400"></div>
                <span className="text-white font-semibold text-sm">{username}</span>
                <button className="px-2 py-0.5 border border-white rounded text-white text-xs">Follow</button>
              </div>
              <p className="text-white text-sm line-clamp-2">
                {caption || 'Your caption will appear here... #reels #viral'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-white text-xs">🎵</span>
                <span className="text-white text-xs truncate">Original audio - {username}</span>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">Instagram Reel Preview</p>
      </div>
    );
  }

  // Regular Post / Carousel Preview
  return (
    <div className="w-full max-w-[320px] mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 p-0.5">
              <div className="w-full h-full rounded-full bg-gray-200"></div>
            </div>
            <div>
              <p className="text-sm font-semibold">{username}</p>
              <p className="text-xs text-gray-500">Location</p>
            </div>
          </div>
          <button className="text-gray-600">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="6" r="1.5"/>
              <circle cx="12" cy="12" r="1.5"/>
              <circle cx="12" cy="18" r="1.5"/>
            </svg>
          </button>
        </div>
        
        {/* Image/Video */}
        <div className={`relative bg-gray-100 ${getAspectRatioClass()}`}>
          {currentMedia.url ? (
            currentMedia.type === 'video' ? (
              <video
                src={currentMedia.url}
                className="w-full h-full object-cover"
                controls
                muted
                playsInline
                key={hasCarouselItems ? currentSlide : 0}
              />
            ) : (
              <img 
                src={currentMedia.url} 
                alt={`Post ${hasCarouselItems ? `slide ${currentSlide + 1}` : ''}`} 
                className="w-full h-full object-cover"
                key={hasCarouselItems ? currentSlide : 0}
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <div className="text-center">
                <span className="text-4xl">📷</span>
                <p className="text-gray-400 text-sm mt-2">
                  {contentType === 'carousel' ? `Carousel (${effectiveSlidesCount} slides)` : 'Image Preview'}
                </p>
              </div>
            </div>
          )}
          
          {/* Carousel Navigation (show if carousel with multiple items OR if carouselItems exist) */}
          {((contentType === 'carousel' || hasCarouselItems) && effectiveSlidesCount > 1) && (
            <>
              {/* Previous Arrow */}
              <button 
                onClick={handlePreviousSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10 hover:scale-110"
                aria-label="Previous slide"
              >
                <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Next Arrow */}
              <button 
                onClick={handleNextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all z-10 hover:scale-110"
                aria-label="Next slide"
              >
                <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Dots Indicator */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                {Array.from({ length: Math.min(effectiveSlidesCount, 10) }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleDotClick(i)}
                    className={`h-2 rounded-full transition-all ${
                      i === currentSlide ? 'bg-white w-6' : 'bg-white/60 hover:bg-white/80 w-2'
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
                {effectiveSlidesCount > 10 && (
                  <span className="text-white text-xs ml-1 flex items-center">+{effectiveSlidesCount - 10}</span>
                )}
              </div>
              
              {/* Slide Counter */}
              <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                {currentSlide + 1} / {effectiveSlidesCount}
              </div>
            </>
          )}
        </div>
        
        {/* Actions */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <button className="hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              <button className="hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </button>
              <button className="hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <button className="hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
          </div>
          
          {/* Likes */}
          <p className="text-sm font-semibold mb-1">1,234 likes</p>
          
          {/* Caption */}
          <p className="text-sm">
            <span className="font-semibold">{username}</span>{' '}
            <span className="text-gray-800">
              {caption ? caption.substring(0, 100) : 'Your caption will appear here...'}
              {caption && caption.length > 100 && '...'}
            </span>
          </p>
          
          {/* Comments Link */}
          <p className="text-sm text-gray-500 mt-1">View all 42 comments</p>
          
          {/* Time */}
          <p className="text-xs text-gray-400 mt-1 uppercase">2 hours ago</p>
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        Instagram {contentType === 'carousel' ? 'Carousel' : 'Post'} Preview
      </p>
    </div>
  );
};

export default InstagramPreview;






